import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { Article } from '@/types';
import { api } from '@/client/lib/api';
import { useMeta } from '@/client/lib/useMeta';
import { BRAND, HOME_ABOUT_LINKS, HOME_HERO_AVIF, HOME_HERO_IMAGE, HOME_SUBJECTS, HOME_STORE_IMAGE } from '@/config/site';
import { SectionHeader } from '@/components/SectionHeader';
import { FramedImage } from '@/components/FramedImage';
import { localizeArticle } from '@/lib/i18n-content';
import { InstagramGrid } from '@/features/instagram/InstagramGrid';
import { Newsletter } from '@/features/newsletter/Newsletter';
import { EditorialCard } from '@/components/ArticleView';
import { useI18n } from '@/client/i18n/I18nContext';
import { CaroWordmark } from '@/components/Logo';
import { lp } from '@/client/i18n/locale';
import { parseInlineLinks } from '@/lib/inlineLinks';

/**
 * Poster-hero candidates. The FIRST entry is the shipped hero: with NO `hero=`
 * param (i.e. every real visitor, dev and prod) the page renders it. Carolina
 * can still compare the others live on dev via the picker — open `/?heropick=1`
 * to reveal the selector bar and click through the looks.
 * All candidates share the SAME size (1122×1402). The shared `.poster__photo`
 * framing is tuned for the shipped `portada` (Caro centred): desktop uses
 * `object-position: 85% 8%` (vertical only — the image fills the full width, so
 * horizontal is moot) plus `transform: translateX(15%)` to nudge her right, off
 * the text scrim; mobile uses `object-position: 0% 5%` (anchored left so she
 * sits in the right half of the narrow crop) with the translate reset. The older
 * `caro_hero_*` looks stay in the dev picker and are framed "good enough" by this.
 */
type HeroVariant = { slug: string; src: string; label: string };
const HERO_VARIANTS: HeroVariant[] = [
  { slug: 'portada', src: HOME_HERO_IMAGE, label: 'Actual · portada (bomber · loft)' },
  { slug: 'dots', src: '/media/caro/caro_hero_dots.jpg?v=3', label: 'Falda de lunares' },
  { slug: 'suit', src: '/media/caro/caro_hero_suit.jpg?v=3', label: 'Traje beige' },
  { slug: 'leather', src: '/media/caro/caro_hero_leather.jpg?v=3', label: 'Blazer · falda de cuero' },
  { slug: 'wrap', src: '/media/caro/caro_hero_wrap.jpg?v=3', label: 'Blusa marfil · falda cruzada' },
  { slug: 'camisa', src: '/media/caro/caro_1.jpeg', label: 'Original · camisa azul' },
];

export function HomePage() {
  useMeta({ title: undefined, path: '/' });
  const { t, locale } = useI18n();
  const [articles, setArticles] = useState<Article[]>([]);
  const [searchParams] = useSearchParams();

  // Hero picker (dev/staging only): revealed by `?heropick=1` or any `?hero=`.
  // Regular visitors never see it and get the shipped `actual` hero unchanged.
  const heroSlug = searchParams.get('hero');
  const showHeroPicker = searchParams.has('heropick') || heroSlug !== null;
  const defaultHero = HERO_VARIANTS[0]!;
  const heroVariant = heroSlug ? HERO_VARIANTS.find((v) => v.slug === heroSlug) ?? null : null;
  const heroSrc = heroVariant ? heroVariant.src : defaultHero.src;
  const heroQuery = (v: HeroVariant) => `/?hero=${v.slug}&heropick=1`;
  // Only the shipped hero has an AVIF. A failed <source> does not fall back to
  // the <img src> on its own (e.g. AVIF not yet uploaded to R2): drop it on error.
  const [avifFailed, setAvifFailed] = useState(false);
  const heroAvif = heroSrc === HOME_HERO_IMAGE && !avifFailed;

  useEffect(() => {
    api.articles().then((a) => setArticles(a.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <>
      {/* JSON-LD (OnlineStore + WebSite) is injected by the Worker into <head>
          (pageMeta), with the checkout's D1 shipping zones: not repeated here. */}

      {/* Poster hero — editorial cover: photo on the right, text overlaid on the
          light left area (replicates the printed portada). Bilingual via i18n. */}
      <section className="poster" aria-label="CARO. Casa de estilo">
        <picture>
          {heroAvif ? <source type="image/avif" srcSet={HOME_HERO_AVIF} /> : null}
          <img
            className="poster__photo"
            src={heroSrc}
            alt="Carolina Collaer, CARO. Casa de estilo"
            onError={heroAvif ? () => setAvifFailed(true) : undefined}
            // Hero is the LCP element: fetch it eagerly at high priority (AVIF
            // preloaded in index.html <head>, painted before JS by the Worker's
            // #root shell). No decoding="async": swapping the shell for this
            // <img> must not leave a blank frame.
            fetchPriority="high"
          />
        </picture>
        <div className="poster__scrim" aria-hidden="true" />
        <div className="poster__inner">
          <div className="poster__text">
            {/* Logo lockup: «Casa de estilo» and the pillars centred under CARO. */}
            <div className="poster__brand">
              <p className="poster__wordmark">
                <CaroWordmark />
              </p>
              <p className="poster__brandsub">{t('home.poster.brandSub')}</p>
              <p className="poster__tagline">{t('home.poster.tagline')}</p>
            </div>

            <h1 className="poster__title">{t('home.poster.title')}</h1>

            <p className="poster__sub">
              {t('home.poster.subLead')}
              <b>{t('home.poster.subAccent')}</b>
            </p>

            <div className="poster__needs">
              {[t('home.poster.choose'), t('home.poster.wear'), t('home.poster.be')].map((w) => (
                <div className="poster__need" key={w}>
                  <span className="poster__need-label">{t('home.poster.needTo')}</span>
                  <span className="poster__need-word">{w}</span>
                </div>
              ))}
            </div>

            <p className="poster__by">
              <span className="poster__by-name">{t('home.poster.closing')}</span>
            </p>

            <div className="poster__cta">
              <Link className="btn" to={lp('/shop')}>{t('home.ctaPrimary')}</Link>
              <Link className="btn btn--ghost" to={lp('/caro')}>{t('home.ctaSecondary')}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Dev-only portada selector. Hidden unless `?heropick=1`/`?hero=` is set,
          so it never reaches real visitors. Lets Carolina compare looks live. */}
      {showHeroPicker && (
        <div className="hero-picker" role="group" aria-label="Elegir portada de la home">
          <div className="hero-picker__head">
            <span className="hero-picker__title">Elige la portada</span>
            <span className="hero-picker__hint">solo visible en dev</span>
          </div>
          <div className="hero-picker__opts">
            {HERO_VARIANTS.map((v) => {
              const active = v.slug === (heroVariant?.slug ?? defaultHero.slug);
              return (
                <Link
                  key={v.slug}
                  to={lp(heroQuery(v))}
                  className={`hero-picker__opt${active ? ' is-active' : ''}`}
                  aria-current={active ? 'true' : undefined}
                >
                  <img className="hero-picker__thumb" src={v.src} alt="" loading="lazy" />
                  <span className="hero-picker__label">{v.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}


      {/* The stores — one image + category name + link each. No item carousels. */}
      <section className="section container">
        <div className="center" style={{ marginBottom: 'var(--space-7)' }}>
          <p className="eyebrow">{t('shop.hubEyebrow')}</p>
          <h2 className="section-title">{t('shop.hubTitle')}</h2>
        </div>
        <div className="home-stores">
          {HOME_SUBJECTS.map((s) => (
            <Link key={s.slug} to={lp(s.href)} className="store-block">
              <div className="store-block__media">
                <FramedImage
                  src={HOME_STORE_IMAGE[s.slug] ?? '/media/caro/carolina-collaer-caro-casa-de-estilo-retrato.webp'}
                  alt={t(`shop.${s.slug}.title`)}
                  loading="lazy"
                  fallback=""
                  sizes="(min-width: 1100px) 25vw, (min-width: 768px) 50vw, 100vw"
                />
                <div className="store-block__overlay">
                  <h3 className="store-block__title">{t(`shop.${s.slug}.title`)}</h3>
                  <span className="store-block__cta">{t('shop.hubCta')}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Who is CARO — short summary with the paths: consulting, shop, follow, letter. */}
      <section className="section container home-about" aria-labelledby="home-about-title">
        <p className="eyebrow">{t('home.about.eyebrow')}</p>
        <h2 className="section-title" id="home-about-title">{t('home.about.title')}</h2>
        {(['home.about.body1', 'home.about.body2'] as const).map((k) => (
          <p className="home-about__body" key={k}>
            {parseInlineLinks(t(k)).map((part, i) =>
              typeof part === 'string' ? (
                part
              ) : part.key === 'instagram' ? (
                <a key={i} href={BRAND.instagramUrl} target="_blank" rel="noopener noreferrer">{part.text}</a>
              ) : (
                <Link key={i} to={lp(HOME_ABOUT_LINKS[part.key] ?? '/')}>{part.text}</Link>
              ),
            )}
          </p>
        ))}
      </section>

      {/* The Edit */}
      <section className="section container">
        <SectionHeader
          title="Edit"
          action={<Link className="btn btn--ghost" to={lp('/the-edit')}>{t('common.readMore')}</Link>}
        />
        {/* Four cards, square photos: same grid and size as the store blocks. */}
        <div className="editorial-grid editorial-grid--hub editorial-grid--square">
          {articles.map((raw) => (
            <EditorialCard key={raw.id} article={localizeArticle(raw, locale)} sizes="(min-width: 1100px) 25vw, (min-width: 768px) 50vw, 100vw" />
          ))}
        </div>
      </section>

      <InstagramGrid />
      <Newsletter />


    </>
  );
}
